import React from 'react';
import { cn } from '@/shared/utils/cn';
import { RadioButton } from '../atoms/RadioButton';
import { useRadioButtonGroup } from '../../hooks/useRadioButtonGroup';
import { RadioButtonGroupProps } from '@/shared/types/radiobutton';

/**
 * Componente RadioButtonGroup - Organismo
 *
 * Un grupo de radio buttons que permite seleccionar una opción de múltiples.
 *
 * @example
 * ```tsx
 * <RadioButtonGroup
 *   options={[
 *     { value: 'option1', label: 'Opción 1' },
 *     { value: 'option2', label: 'Opción 2' },
 *     { value: 'option3', label: 'Opción 3' }
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 *   color="blue"
 *   size="md"
 * />
 * ```
 */
export const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
  options,
  value,
  onChange,
  size = 'xs',
  color = 'blue',
  disabled = false,
  orientation = 'vertical',
  className,
  name,
}) => {
  const { selectedValue, handleChange } = useRadioButtonGroup({
    value,
    onChange,
    options,
  });

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(event.target.value);
  };

  return (
    <div
      className={cn(
        'ui-imj',
        'ui-flex ui-gap-3',
        orientation === 'horizontal' ? 'ui-flex-row' : 'ui-flex-col',
        className
      )}
      role="radiogroup"
      aria-label={name}
    >
      {options.map((option) => (
        <RadioButton
          key={option.value}
          value={option.value}
          label={option.label}
          checked={selectedValue === option.value}
          onChange={handleRadioChange}
          size={size}
          color={color}
          disabled={disabled || option.disabled}
          name={name}
        />
      ))}
    </div>
  );
};
